import { useState, useEffect } from "react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate, Link, useLocation } from "react-router-dom";
import "../../styles/components/forms.css";
import "../../styles/user/login.css";
import axios from "axios";
import { getRecaptchaToken, loadRecaptcha } from "../../utils/recaptcha";
import { tryOpenAppThenStore, APP_STORE_URL, PLAY_STORE_URL } from "../../utils/appStoreLinks";

const Login = () => {
  const [credentials, setCredentials] = useState({ email: "", password: "" });
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [urlDiscountCode, setUrlDiscountCode] = useState("");
  const [checkoutTierId, setCheckoutTierId] = useState("");
  const [checkoutInfo, setCheckoutInfo] = useState({
    productName: '',
    tierLabel: '',
    priceAmount: '',
    discountPercentage: ''
  });
  const [autoCheckoutLoading, setAutoCheckoutLoading] = useState(false);
  const [autoCheckoutIntent, setAutoCheckoutIntent] = useState(false);
  const { login, user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const codeFromUrl = params.get("discount_code");
    const tierIdFromUrl = params.get("price_tier_id");
    const productName = params.get("product_name");
    const tierLabel = params.get("tier_label");
    const priceAmount = params.get("price_amount");
    const discountPercentage = params.get("discount_percentage");
    if (codeFromUrl && codeFromUrl.trim() !== "") {
      const normalized = codeFromUrl
        .trim()
        .replace(/^"+|"+$/g, "")
        .toUpperCase();
      setUrlDiscountCode(normalized);
      sessionStorage.setItem("pendingDiscountCode", normalized);
      localStorage.setItem("pendingDiscountCode", normalized);
    }
    const normalizedTierId = tierIdFromUrl ? String(tierIdFromUrl).trim() : "";
    setCheckoutTierId(normalizedTierId);
    setAutoCheckoutIntent(Boolean(normalizedTierId));
    setCheckoutInfo({
      productName: productName ? String(productName).trim() : '',
      tierLabel: tierLabel ? String(tierLabel).trim() : '',
      priceAmount: priceAmount ? String(priceAmount).trim() : '',
      discountPercentage: discountPercentage ? String(discountPercentage).trim() : ''
    });
  }, [location.search]);

  useEffect(() => {
    const trialWarning = localStorage.getItem('trialDiscountWarning');
    if (trialWarning) {
      setError(trialWarning);
      localStorage.removeItem('trialDiscountWarning');
    }
  }, []);

  useEffect(() => {
    // Wait for loading to complete, then redirect if user is authenticated
    if (!loading && user && !autoCheckoutIntent) {
      navigate("/dashboard", { replace: true });
    }
    
    // Pre-load reCAPTCHA script when component mounts
    if (process.env.REACT_APP_RECAPTCHA_SITE_KEY) {
      loadRecaptcha().catch(err => {
        console.warn('reCAPTCHA pre-load failed:', err);
      });
    }
  }, [user, loading, navigate, autoCheckoutIntent]);

  const createDirectCheckout = async (userId, accessToken) => {
    if (!checkoutTierId || !userId) return null;
    const headers = {};
    if (accessToken) {
      headers.Authorization = `Bearer ${accessToken}`;
    }
    const code = (
      urlDiscountCode ||
      sessionStorage.getItem('pendingDiscountCode') ||
      localStorage.getItem('pendingDiscountCode') ||
      ''
    )
      .trim()
      .replace(/^"+|"+$/g, '')
      .toUpperCase();
    const response = await axios.post(
      `${process.env.REACT_APP_BACKEND_URL}/api/pricing-plans/create`,
      { userId, priceId: checkoutTierId, ...(code ? { discountCode: code } : {}) },
      { withCredentials: true, headers }
    );
    return response.data?.checkoutUrl || null;
  };

  const buildCreateAccountUrlWithCheckoutParams = () => {
    const params = new URLSearchParams();
    if (urlDiscountCode) params.set('discount_code', urlDiscountCode);
    if (checkoutTierId) params.set('price_tier_id', checkoutTierId);
    if (checkoutInfo.productName) params.set('product_name', checkoutInfo.productName);
    if (checkoutInfo.tierLabel) params.set('tier_label', checkoutInfo.tierLabel);
    if (checkoutInfo.priceAmount) params.set('price_amount', checkoutInfo.priceAmount);
    if (checkoutInfo.discountPercentage) params.set('discount_percentage', checkoutInfo.discountPercentage);
    const query = params.toString();
    return query ? `/create-account?${query}` : '/create-account';
  };

  const openApp = (e) => {
    e.preventDefault();
    tryOpenAppThenStore("lopeglad://");
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      // Get reCAPTCHA token (optional for login, but good for security)
      const recaptchaToken = await getRecaptchaToken('login');
      
      const response = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/auth/login`,
        {
          ...credentials,
          discountCode:
            urlDiscountCode ||
            sessionStorage.getItem("pendingDiscountCode") ||
            localStorage.getItem("pendingDiscountCode") ||
            undefined,
          recaptchaToken: recaptchaToken || undefined // Only send if available
        },
        { withCredentials: true }
      );
      const data = response.data;
      if (data.success) {
        setSuccess(data.message);
        setError("");
        // Let AuthContext handle the user state
        await login(data.user);
        if (checkoutTierId && data.user?.id) {
          setAutoCheckoutLoading(true);
          setSuccess('Innlogging vellykket. Sender deg videre til betaling...');
          try {
            const checkoutUrl = await createDirectCheckout(data.user.id, data.token);
            if (checkoutUrl) {
              window.location.href = checkoutUrl;
              return;
            }
            setError('Innlogging vellykket, men kunne ikke starte betaling automatisk.');
          } catch (checkoutError) {
            setError('Innlogging vellykket, men automatisk videresending til betaling feilet.');
          } finally {
            setAutoCheckoutLoading(false);
          }
        }
        navigate("/dashboard", { replace: true });
      } else {
        setError(data.message);
      }
    } catch (error) {
      setError(error.response?.data?.message || "Innlogging feilet");
    }
  };

  // Show loading state while checking authentication
  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Laster...</p>
      </div>
    );
  }

  // If user is already authenticated, show loading while redirecting
  if (user) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Omdirigerer til dashboard...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="login-container">
        <form onSubmit={handleSubmit} className="login-form">
          <h2>Logg inn</h2>
          {urlDiscountCode && (
            <div className="success-message">Rabattkode fra lenke: {urlDiscountCode}</div>
          )}
          {(checkoutTierId || checkoutInfo.priceAmount || checkoutInfo.productName) && (
            <div className="success-message" style={{ textAlign: 'left' }}>
              <strong>Registrer før betaling:</strong>{' '}
              Etter innlogging blir du sendt direkte til Stripe for betaling.
              {checkoutInfo.productName && <span> Produkt: {checkoutInfo.productName}</span>}
              {checkoutInfo.tierLabel && <span> ({checkoutInfo.tierLabel})</span>}
              {checkoutInfo.priceAmount && <span> Pris før rabatt: NOK {checkoutInfo.priceAmount}.</span>}
              {checkoutInfo.discountPercentage && checkoutInfo.priceAmount && !Number.isNaN(Number(checkoutInfo.priceAmount)) && !Number.isNaN(Number(checkoutInfo.discountPercentage)) && (
                <span> Med {checkoutInfo.discountPercentage}% rabatt: NOK {(Number(checkoutInfo.priceAmount) * (1 - Number(checkoutInfo.discountPercentage) / 100)).toFixed(2)}.</span>
              )}
            </div>
          )}
          {error && <div className="error-message">{error}</div>}
          {success && <div className="success-message">{success}</div>}
          {autoCheckoutLoading && <div className="success-message">Omdirigerer til Stripe...</div>}
          <input
            type="email"
            placeholder="Epost"
            value={credentials.email}
            onChange={(e) =>
              setCredentials({ ...credentials, email: e.target.value })
            }
          />
          <div className="password-input-wrapper">
            <input
              type={showPassword ? "text" : "password"}
              placeholder="Passord"
              value={credentials.password}
              onChange={(e) =>
                setCredentials({ ...credentials, password: e.target.value })
              }
            />
            <span
              className="password-toggle"
              onClick={() => setShowPassword(!showPassword)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  setShowPassword(!showPassword);
                }
              }}
              role="button"
              tabIndex={0}
              aria-label={showPassword ? "Skjul passord" : "Vis passord"}
              title={showPassword ? "Skjul passord" : "Vis passord"}
            >
              {showPassword ? (
                <span aria-hidden="true">👁️</span>
              ) : (
                <span aria-hidden="true">👁️‍🗨️</span>
              )}
            </span>
          </div>
          <div className="forgot-password-link">
            <Link to="/forgot-password">Glemt passord?</Link>
          </div>
          <button type="submit">Logg inn</button>
          <button 
            type="button"
            onClick={openApp}
            className="open-app-button"
          >
            Åpne i appen
          </button>
          <p className="store-links-hint">
            Har du ikke appen? Last ned på{' '}
            <a href={APP_STORE_URL} target="_blank" rel="noopener noreferrer">App Store</a>
            {' · '}
            <a href={PLAY_STORE_URL} target="_blank" rel="noopener noreferrer">Google Play</a>
          </p>
          <Link className="create-account-link" to={buildCreateAccountUrlWithCheckoutParams()}>
            Opprett konto
          </Link>
          {/* reCAPTCHA v3 badge - required by Google */}
          {process.env.REACT_APP_RECAPTCHA_SITE_KEY && (
            <div className="recaptcha-notice">
              <small>
                This site is protected by reCAPTCHA and the Google{' '}
                <a href="https://policies.google.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
                {' '}and{' '}
                <a href="https://policies.google.com/terms" target="_blank" rel="noopener noreferrer">Terms of Service</a>
                {' '}apply.
              </small>
            </div>
          )}
        </form>
      </div>
    </div>
  );
};

export default Login;
